import { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Paper, 
  Typography, 
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { baseClient } from '../api/baseClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Define the ChatMessage interface
export interface ChatMessage {
  id: string;
  room_id: string;
  author_name: string;
  text: string;
  created_at: string;
}

interface TeamChatProps {
  dealId: string;
}

// Function to get the room ID for a deal
const fetchDealRoom = async (dealId: string): Promise<{ id: string }> => {
  const response = await baseClient.get(`/deals/${dealId}/room`);
  return { id: response.data.deal_room.id };
};

// Function to post a new message
const postMessage = async ({ roomId, message, authorName }: { roomId: string; message: string; authorName: string }): Promise<ChatMessage> => {
  const response = await baseClient.post('/chat-messages/', {
    room_id: roomId,
    author_name: authorName,
    text: message
  });
  return response.data;
};

// Function to send a message to the AI and get a response
const sendMessageToAI = async ({ message, dealId }: { message: string; dealId: string }): Promise<string> => {
  const response = await baseClient.post('/conversation/stream', {
    message,
    context: { dealId }
  });
  return response.data.response;
};

export default function TeamChat({ dealId }: TeamChatProps) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isAiTyping, setIsAiTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  
  // Get the current user's name (in a real app, this would come from auth)
  const currentUser = "Current User"; // Replace with actual user name from auth
  
  // Query to get the room ID
  const { data: roomData, isLoading: isRoomLoading } = useQuery(
    ['dealRoom', dealId],
    () => fetchDealRoom(dealId),
    {
      enabled: !!dealId,
      staleTime: Infinity, // Room ID shouldn't change
    }
  );
  
  // Query to get messages
  const { data: latestMessages } = useQuery(
    ['chatMessages', roomData?.id],
    async () => {
      if (!roomData?.id) return [];
      const response = await baseClient.get(`/deal-rooms/${roomData.id}/messages`);
      return response.data;
    },
    {
      enabled: !!roomData?.id,
      refetchInterval: 5000, // Poll for new messages every 5 seconds
    }
  );
  
  // Update local messages when query data changes
  useEffect(() => {
    if (latestMessages) {
      setMessages(latestMessages);
    }
  }, [latestMessages]);
  
  // Mutation to post a new message
  const postMessageMutation = useMutation(postMessage, {
    onSuccess: () => {
      // Invalidate and refetch the messages query
      queryClient.invalidateQueries(['chatMessages', roomData?.id]);
    },
  });
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const handleSendMessage = async () => {
    if (!message.trim() || !roomData?.id) return;
    
    // Add user message to the chat
    const userMessage = message;
    setMessage('');
    
    // Post the user's message to the backend
    postMessageMutation.mutate({
      roomId: roomData.id,
      message: userMessage,
      authorName: currentUser
    });
    
    // If the message is directed to the AI (starts with @AI)
    if (userMessage.toLowerCase().startsWith('@ai')) {
      setIsAiTyping(true);
      try {
        // Send the message to the AI and get a response
        const aiResponse = await sendMessageToAI({
          message: userMessage.substring(3).trim(), // Remove the @AI prefix
          dealId
        });
        
        // Post the AI's response to the backend
        postMessageMutation.mutate({
          roomId: roomData.id,
          message: aiResponse,
          authorName: "Deal Steward AI"
        });
      } catch (error) {
        console.error('Error getting AI response:', error);
        // Post an error message if the AI request fails
        postMessageMutation.mutate({
          roomId: roomData.id,
          message: "I'm sorry, I couldn't process that request right now.",
          authorName: "Deal Steward AI"
        });
      } finally {
        setIsAiTyping(false);
      }
    }
  };
  
  if (isRoomLoading) {
    return <CircularProgress />;
  }
  
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      p: 2 
    }}>
      {/* Messages area */}
      <Box sx={{ 
        flexGrow: 1, 
        overflowY: 'auto', 
        mb: 2,
        bgcolor: 'background.default',
        borderRadius: 1,
        p: 1
      }}>
        <List>
          {messages.map((msg, index) => (
            <Box key={msg.id || index}>
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary={
                    <Typography
                      component="span"
                      variant="body1"
                      color="text.primary"
                      sx={{ display: 'inline', fontWeight: 'bold' }}
                    >
                      {msg.author_name}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {msg.text}
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        {new Date(msg.created_at).toLocaleTimeString()}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
              {index < messages.length - 1 && <Divider variant="inset" component="li" />}
            </Box>
          ))}
          {isAiTyping && (
            <ListItem>
              <ListItemText
                primary="Deal Steward AI"
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="body2">Typing...</Typography>
                  </Box>
                }
              />
            </ListItem>
          )}
        </List>
        <div ref={messagesEndRef} />
      </Box>
      
      {/* Message input */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center'
      }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type your message... (Use @AI to ask the AI)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          size="small"
          sx={{ mr: 1 }}
        />
        <Button 
          variant="contained" 
          color="primary" 
          endIcon={<SendIcon />}
          onClick={handleSendMessage}
          disabled={!message.trim() || postMessageMutation.isPending}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
} 